/**
 * ImpersonationBanner - Banner shown when super admin is impersonating a user
 * Extracted from App.js Dashboard component for maintainability
 */
import React from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Shield } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Get the impersonated user's display name
 */
const getImpersonatedUserName = () => {
  // Migration-aware: prefer sessionStorage, fall back to legacy localStorage
  const email =
    sessionStorage.getItem('impersonated_user_email') ||
    localStorage.getItem('impersonated_user_email') ||
    'Unknown User';
  try {
    const userStr = sessionStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    return user?.full_name || email;
  } catch {
    return email;
  }
};

/**
 * ImpersonationBanner Component
 * @param {function} toast - Toast notification function
 */
const ImpersonationBanner = ({ toast }) => {
  const isImpersonating =
    sessionStorage.getItem('is_impersonating') === 'true' ||
    localStorage.getItem('is_impersonating') === 'true';
  
  if (!isImpersonating) return null;

  const handleExitImpersonation = async () => {
    try {
      const token = sessionStorage.getItem('token');
      
      // Call end impersonation endpoint
      await axios.post(
        `${API_URL}/api/super-admin/end-impersonation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Restore original token and user
      const originalToken = sessionStorage.getItem('original_token');
      const originalUser = sessionStorage.getItem('original_user') || localStorage.getItem('original_user');
      
      if (originalToken && originalUser) {
        // SECURITY: keep auth tokens in sessionStorage only (not localStorage).
        sessionStorage.setItem('token', originalToken);
        sessionStorage.setItem('authToken', originalToken);
        sessionStorage.setItem('user', originalUser);
      }
      
      // Clear impersonation flags
      sessionStorage.removeItem('is_impersonating');
      sessionStorage.removeItem('impersonated_user_email');
      sessionStorage.removeItem('original_token');
      sessionStorage.removeItem('original_user');
      // Also clear any legacy localStorage leftovers from pre-migration sessions
      localStorage.removeItem('is_impersonating');
      localStorage.removeItem('impersonated_user_email');
      localStorage.removeItem('authToken');
      localStorage.removeItem('original_user');
      
      toast.success('✅ Exited impersonation mode - Returning to Super Admin Panel');
      
      // Redirect to admin panel
      setTimeout(() => {
        window.location.href = '/platform/admin';
      }, 500);
    } catch (error) {
      console.error('Error ending impersonation:', error);
      toast.error('Failed to exit impersonation. Refreshing page...');
      
      // Force clear and refresh on error
      localStorage.removeItem('is_impersonating');
      localStorage.removeItem('impersonated_user_email');
      setTimeout(() => window.location.href = '/platform/admin', 1000);
    }
  };

  return (
    <div className="sticky top-0 z-50 bg-gradient-to-r from-orange-500 via-orange-600 to-red-600 text-white px-6 py-4 shadow-2xl border-b-4 border-orange-700">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="animate-pulse">
              <Shield className="w-6 h-6" />
            </div>
            <div className="flex flex-col">
              <div className="flex items-center space-x-2">
                <span className="font-bold text-lg">
                  🎭 IMPERSONATION MODE ACTIVE
                </span>
                <span className="px-2 py-0.5 bg-yellow-400 text-orange-900 text-xs font-bold rounded">
                  VIEWING AS
                </span>
              </div>
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-white font-semibold">
                  {getImpersonatedUserName()}
                </span>
                <span className="text-orange-200 text-sm">
                  ({localStorage.getItem('impersonated_user_email')})
                </span>
              </div>
              <span className="text-orange-100 text-xs mt-0.5">
                ⚠️ All actions are audited for HIPAA compliance • Session expires in 1 hour
              </span>
            </div>
          </div>
          <Button
            onClick={handleExitImpersonation}
            className="bg-white text-orange-600 hover:bg-orange-50 font-bold border-2 border-orange-300 shadow-lg transition-all hover:scale-105"
            size="lg"
          >
            🚪 Exit Impersonation
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
